import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '@axios';
import { RootState } from '@redux/reducers';
import { AppDispatch } from '@redux/store';


export interface ImportCotasStatusResponse {
    jobId: string;
    status: 'AGUARDANDO' | 'PROCESSANDO' | 'CONCLUIDO' | 'ERRO';
    linhasProcessadas: number;
    erros: string[];
    iniciadoEm: string | null;
    finalizadoEm: string | null;
}

export const fetchImportCotasStatus = createAsyncThunk<
    ImportCotasStatusResponse,
    string
>('importCotas/fetchStatus', async (jobId, thunkAPI) => {
    try {
        const response = await axios.get<ImportCotasStatusResponse>(
            `/cotas-cliente/importar/status/${jobId}`,
        );

        const { data, status } = response;

        if (status === 200) {
            return data;
        }

        return thunkAPI.rejectWithValue('Erro ao buscar status do import');
    } catch (e) {
        return thunkAPI.rejectWithValue('Erro ao buscar status do import');
    }
});

let pollingInterval: ReturnType<typeof setInterval> | null = null;

export const startImportCotasPolling =
    (jobId: string, fileName?: string) => (dispatch: AppDispatch) => {
        dispatch(setIsImporting(true));
        dispatch(setFileName(fileName ?? null));
        dispatch(setJobInfo({ jobId, status: 'PROCESSANDO', linhasProcessadas: 0, erros: [], iniciadoEm: null, finalizadoEm: null }));

        if (pollingInterval) {
            clearInterval(pollingInterval);
        }

        // Busca imediatamente e depois a cada 5s
        dispatch(fetchImportCotasStatus(jobId));

        pollingInterval = setInterval(() => {
            dispatch(fetchImportCotasStatus(jobId));
        }, 10000);
    };

export const stopImportCotasPolling = () => () => {
    if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
    }
};

export const resumeImportCotasPolling =
    () => (dispatch: AppDispatch, getState: () => RootState) => {
        const { isImporting, jobInfo } = getState().importCotas;

        const shouldResume =
            isImporting ||
            jobInfo?.status === 'AGUARDANDO' ||
            jobInfo?.status === 'PROCESSANDO';

        if (!shouldResume || !jobInfo?.jobId) return;

        dispatch(startImportCotasPolling(jobInfo.jobId));
    };

export const clearImportCotas = () => (dispatch: AppDispatch) => {
    if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
    }
    dispatch(clearImportCotasState());
};

interface IImportCotasState {
    isImporting: boolean;
    jobInfo: ImportCotasStatusResponse | null;
    isFetchingStatus: boolean;
    isError: boolean;
    fileName: string | null;
}

const initialState: IImportCotasState = {
    isImporting: false,
    jobInfo: null,
    isFetchingStatus: false,
    isError: false,
    fileName: null,
};

const importCotasSlice = createSlice({
    name: 'importCotasSlice',
    initialState,
    reducers: {
        setIsImporting: (state, action: PayloadAction<boolean>) => {
            state.isImporting = action.payload;
            return state;
        },
        setJobInfo: (state, action: PayloadAction<ImportCotasStatusResponse>) => {
            state.jobInfo = action.payload;
            return state;
        },
        setFileName: (state, action: PayloadAction<string | null>) => {
            state.fileName = action.payload;
            return state;
        },
        clearImportCotasState: () => initialState,
    },
    extraReducers: {
        [fetchImportCotasStatus.pending.toString()]: (state) => {
            state.isFetchingStatus = true;
            state.isError = false;
            return state;
        },
        [fetchImportCotasStatus.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<ImportCotasStatusResponse>,
        ) => {
            state.isFetchingStatus = false;
            state.jobInfo = payload;

            // Para o import quando terminar (com sucesso ou erro)
            if (payload.status === 'CONCLUIDO' || payload.status === 'ERRO') {
                state.isImporting = false;
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    pollingInterval = null;
                }
            }

            return state;
        },
        [fetchImportCotasStatus.rejected.toString()]: (state) => {
            state.isFetchingStatus = false;
            state.isError = true;
            state.isImporting = false;

            if (pollingInterval) {
                clearInterval(pollingInterval);
                pollingInterval = null;
            }

            return state;
        },
    },
});

export const importCotasSelector = (state: RootState): IImportCotasState =>
    state.importCotas;

export const { setIsImporting, setJobInfo, setFileName, clearImportCotasState } =
    importCotasSlice.actions;

export default importCotasSlice.reducer;
